import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import VerifiedBadge from "../components/VerifiedBadge";
import ShortlistButton from "../components/ShortlistButton";
import { useAuth } from "../context/AuthContext";
import { Search, SlidersHorizontal, X, BookmarkPlus, Bell, ArrowLeftRight } from "lucide-react";

const POSITIONS = ["GK", "CB", "LB", "RB", "CDM", "CM", "CAM", "LW", "RW", "ST"];
const SEASONS = ["2025/26", "2024/25", "2023/24", "2022/23", "2021/22"];
const AVAIL_DOTS = { green: "bg-green-500", yellow: "bg-yellow-400", red: "bg-red-500" };
const AVAIL_LABELS = { green: "Looking for a club", yellow: "Open to offers", red: "Not looking" };

export default function DiscoverPage() {
  const { user: me } = useAuth();
  const isScout = me?.role === "scout" || me?.role === "coach";
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [regions, setRegions] = useState({});
  const [savedSearches, setSavedSearches] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const position = params.get("position") || "";
  const team = params.get("team") || "";
  const season = params.get("season") || "";
  const minGoals = params.get("min_goals") || "";
  const region = params.get("region") || "";
  const foot = params.get("preferred_foot") || "";
  const level = params.get("level") || "";
  const availability = params.get("availability") || "";
  const minAge = params.get("min_age") || "";
  const maxAge = params.get("max_age") || "";
  const minHeight = params.get("min_height") || "";
  const maxHeight = params.get("max_height") || "";
  const minGa90 = params.get("min_ga_per90") || "";
  const minMpg = params.get("min_minutes_per_game") || "";
  const statsActive = Boolean(season || minGoals || minGa90 || minMpg);
  const anyFilter = Boolean(
    position || team || season || minGoals || region || foot || level || availability ||
    minAge || maxAge || minHeight || maxHeight || minGa90 || minMpg
  );

  useEffect(() => {
    api.get("/teams").then(({ data }) => setTeams(data)).catch(() => {});
    api.get("/regions").then(({ data }) => setRegions(data.Australia || {})).catch(() => {});
    api.get("/saved-searches").then(({ data }) => setSavedSearches(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isScout) return;
    api.get("/shortlists").then(({ data }) => setShortlists(data)).catch(() => {});
  }, [isScout]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const qs = new URLSearchParams();
      for (const [key, value] of params.entries()) {
        if (value) qs.set(key, value);
      }
      const { data } = await api.get(`/users${qs.toString() ? `?${qs}` : ""}`);
      setUsers(data);
      setLoading(false);
    };
    load();
  }, [params]);

  const setFilter = (key, value) => {
    const next = Object.fromEntries(params.entries());
    if (value) next[key] = value;
    else delete next[key];
    setParams(next);
  };

  const clearFilters = () => {
    const next = {};
    if (params.get("q")) next.q = params.get("q");
    setParams(next);
  };

  const submit = (e) => {
    e.preventDefault();
    const next = Object.fromEntries(params.entries());
    if (q) next.q = q;
    else delete next.q;
    setParams(next);
  };

  const toggle = async (u) => {
    if (u.is_following) await api.post(`/users/${u.id}/unfollow`);
    else await api.post(`/users/${u.id}/follow`);
    setUsers((list) =>
      list.map((x) => (x.id === u.id ? { ...x, is_following: !x.is_following } : x))
    );
  };

  const saveSearch = async () => {
    setSaving(true);
    try {
      const payload = {
        q: params.get("q") || "",
        position,
        team,
        season,
        min_goals: minGoals ? Number(minGoals) : 0,
        region,
        preferred_foot: foot,
        level,
        availability,
        min_age: minAge ? Number(minAge) : 0,
        max_age: maxAge ? Number(maxAge) : 0,
        min_height: minHeight ? Number(minHeight) : 0,
        max_height: maxHeight ? Number(maxHeight) : 0,
        min_ga_per90: minGa90 ? Number(minGa90) : 0,
        min_minutes_per_game: minMpg ? Number(minMpg) : 0,
      };
      const { data } = await api.post("/saved-searches", payload);
      setSavedSearches((list) => [data, ...list.filter((s) => s.id !== data.id)]);
      toast.success("Saved search added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save search");
    } finally {
      setSaving(false);
    }
  };

  const applySearch = (s) => {
    const f = s.filters || {};
    const next = {};
    if (f.q) next.q = f.q;
    if (f.position) next.position = f.position;
    if (f.team) next.team = f.team;
    if (f.season) next.season = f.season;
    if (f.region) next.region = f.region;
    if (f.preferred_foot) next.preferred_foot = f.preferred_foot;
    if (f.level) next.level = f.level;
    if (f.availability) next.availability = f.availability;
    for (const k of ["min_goals", "min_age", "max_age", "min_height", "max_height", "min_ga_per90", "min_minutes_per_game"]) {
      if (f[k] && Number(f[k]) > 0) next[k] = String(f[k]);
    }
    setQ(f.q || "");
    setParams(next);
  };

  const deleteSearch = async (s) => {
    try {
      await api.delete(`/saved-searches/${s.id}`);
      setSavedSearches((list) => list.filter((x) => x.id !== s.id));
      toast.success("Saved search removed");
    } catch (err) {
      toast.error("Could not delete saved search");
    }
  };

  const selectCls =
    "bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-2 rounded-sm text-sm text-white transition-colors";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="discover-page">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="font-display text-4xl uppercase tracking-tight text-white">Discover players</h1>
        <Link
          to="/compare"
          data-testid="discover-compare-link"
          className="flex items-center gap-2 px-4 py-2 border border-[#404040] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors text-sm shrink-0"
        >
          <ArrowLeftRight className="w-4 h-4" />
          Compare
        </Link>
      </div>
      <p className="text-sm text-[#a3a3a3] mb-6">Filter by position, team, region, age, height, foot, level and per-90 output.</p>

      <form onSubmit={submit} className="mb-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[#737373] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="discover-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'striker' or 'Arsenal'…"
            className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none pl-12 pr-4 py-3 rounded-sm text-white transition-colors"
          />
        </div>
      </form>

      <div
        data-testid="discover-filters"
        className="flex flex-wrap items-center gap-2 mb-6 bg-[#121212] border border-[#262626] p-3 rounded-sm"
      >
        <SlidersHorizontal className="w-4 h-4 text-[#737373] shrink-0" />
        <select
          data-testid="filter-position"
          value={position}
          onChange={(e) => setFilter("position", e.target.value)}
          className={selectCls}
        >
          <option value="">All positions</option>
          {POSITIONS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          data-testid="filter-team"
          value={team}
          onChange={(e) => setFilter("team", e.target.value)}
          className={selectCls}
        >
          <option value="">All teams</option>
          {teams.map((t) => (
            <option key={t.team} value={t.team}>{t.team}</option>
          ))}
        </select>
        <select
          data-testid="filter-season"
          value={season}
          onChange={(e) => setFilter("season", e.target.value)}
          className={selectCls}
        >
          <option value="">Any season</option>
          {SEASONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          data-testid="filter-min-goals"
          type="number"
          min="0"
          max="99"
          value={minGoals}
          onChange={(e) => setFilter("min_goals", e.target.value)}
          placeholder="Goals ≥"
          className={`${selectCls} w-24`}
        />
        <select
          data-testid="filter-region"
          value={region}
          onChange={(e) => setFilter("region", e.target.value)}
          className={`${selectCls} max-w-[190px]`}
        >
          <option value="">All regions (AU)</option>
          {Object.entries(regions).map(([state, areas]) => (
            <optgroup key={state} label={state}>
              {areas.map((a) => (
                <option key={a} value={`${state} - ${a}`}>{`${state} - ${a}`}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          data-testid="filter-foot"
          value={foot}
          onChange={(e) => setFilter("preferred_foot", e.target.value)}
          className={selectCls}
        >
          <option value="">Any foot</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="both">Both</option>
        </select>
        <select
          data-testid="filter-level"
          value={level}
          onChange={(e) => setFilter("level", e.target.value)}
          className={selectCls}
        >
          <option value="">Any level</option>
          <option value="amateur">Amateur</option>
          <option value="semi-pro">Semi-pro</option>
          <option value="pro">Pro</option>
        </select>
        <select
          data-testid="filter-availability"
          value={availability}
          onChange={(e) => setFilter("availability", e.target.value)}
          className={selectCls}
        >
          <option value="">Any status</option>
          <option value="green">Green · Looking for a club</option>
          <option value="yellow">Yellow · Open to offers</option>
          <option value="red">Red · Not looking</option>
        </select>
        <div className="flex items-center gap-1">
          <input
            data-testid="filter-min-age"
            type="number" min="5" max="60"
            value={minAge}
            onChange={(e) => setFilter("min_age", e.target.value)}
            placeholder="Age ≥"
            className={`${selectCls} w-[84px]`}
          />
          <span className="text-[#525252] text-xs">–</span>
          <input
            data-testid="filter-max-age"
            type="number" min="5" max="60"
            value={maxAge}
            onChange={(e) => setFilter("max_age", e.target.value)}
            placeholder="Age ≤"
            className={`${selectCls} w-[72px]`}
          />
        </div>
        <div className="flex items-center gap-1">
          <input
            data-testid="filter-min-height"
            type="number" min="100" max="230"
            value={minHeight}
            onChange={(e) => setFilter("min_height", e.target.value)}
            placeholder="Cm ≥"
            className={`${selectCls} w-[94px]`}
          />
          <span className="text-[#525252] text-xs">–</span>
          <input
            data-testid="filter-max-height"
            type="number" min="100" max="230"
            value={maxHeight}
            onChange={(e) => setFilter("max_height", e.target.value)}
            placeholder="Cm ≤"
            className={`${selectCls} w-[72px]`}
          />
        </div>
        <input
          data-testid="filter-ga90"
          type="number" min="0" step="0.1"
          value={minGa90}
          onChange={(e) => setFilter("min_ga_per90", e.target.value)}
          placeholder="G+A/90 ≥"
          className={`${selectCls} w-[104px]`}
        />
        <input
          data-testid="filter-mpg"
          type="number" min="0" max="120"
          value={minMpg}
          onChange={(e) => setFilter("min_minutes_per_game", e.target.value)}
          placeholder="Mins/gm ≥"
          className={`${selectCls} w-[112px]`}
        />
        {anyFilter && (
          <button
            type="button"
            data-testid="filter-clear"
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest text-[#a3a3a3] hover:text-white border border-[#262626] hover:border-white rounded-sm transition-colors"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
        {(anyFilter || params.get("q")) && (
          <button
            type="button"
            data-testid="save-search-btn"
            onClick={saveSearch}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest bg-white text-black font-semibold hover:bg-gray-200 rounded-sm transition-colors disabled:opacity-60 ml-auto"
          >
            <BookmarkPlus className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save search"}
          </button>
        )}
      </div>

      {savedSearches.length > 0 && (
        <div className="mb-6" data-testid="saved-searches">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#737373] mb-2">
            <Bell className="w-3 h-3" />
            Saved searches — you get an alert when new players match
          </div>
          <div className="flex flex-wrap gap-2">
            {savedSearches.map((s) => (
              <span
                key={s.id}
                data-testid={`saved-search-${s.id}`}
                className="inline-flex items-center gap-2 bg-[#121212] border border-[#262626] hover:border-white rounded-sm text-sm text-white transition-colors"
              >
                <button
                  type="button"
                  data-testid={`saved-search-apply-${s.id}`}
                  onClick={() => applySearch(s)}
                  className="pl-3 py-1.5 hover:underline"
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  data-testid={`saved-search-delete-${s.id}`}
                  onClick={() => deleteSearch(s)}
                  className="pr-2 py-1.5 text-[#737373] hover:text-white"
                  aria-label={`Delete saved search ${s.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#737373]">Searching…</div>
      ) : users.length === 0 ? (
        <div data-testid="discover-empty" className="text-[#737373] bg-[#121212] border border-[#262626] p-6 rounded-sm">
          No players match these filters.
        </div>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li
              key={u.id}
              data-testid={`discover-user-${u.id}`}
              className="flex items-center gap-4 bg-[#121212] border border-[#262626] p-4 rounded-sm hover:bg-[#1a1a1a] transition-colors"
            >
              <Link to={`/profile/${u.id}`}>
                <Avatar url={u.avatar_url} name={u.name} size={52} />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/profile/${u.id}`} className="text-white font-semibold hover:underline inline-flex items-center gap-1.5">
                  {u.name}
                  <VerifiedBadge role={u.role} verified={u.verified} />
                  {AVAIL_DOTS[u.availability] && (
                    <span
                      data-testid={`discover-availability-${u.id}`}
                      title={AVAIL_LABELS[u.availability]}
                      className={`w-2.5 h-2.5 rounded-full shrink-0 ${AVAIL_DOTS[u.availability]}`}
                    />
                  )}
                </Link>
                <div className="text-xs text-[#a3a3a3]">
                  {u.role && u.role !== "player"
                    ? u.role.charAt(0).toUpperCase() + u.role.slice(1)
                    : u.position || "Player"}
                  {u.team ? (
                    <>
                      {" · "}
                      <Link
                        to={`/team/${encodeURIComponent(u.team)}`}
                        className="hover:text-white hover:underline"
                      >
                        {u.team}
                      </Link>
                    </>
                  ) : null}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                  {[
                    u.region,
                    u.age ? `Age ${u.age}` : null,
                    u.height_cm ? `${u.height_cm} cm` : null,
                    u.preferred_foot ? `${u.preferred_foot} foot` : null,
                    u.level_of_play,
                  ].filter(Boolean).join(" · ") || null}
                  {(u.region || u.age || u.height_cm || u.preferred_foot || u.level_of_play) && " · "}
                  {u.followers_count} followers
                </div>
              </div>
              {statsActive && u.season_goals !== undefined && (
                <div
                  data-testid={`discover-goals-${u.id}`}
                  className="text-center bg-[#0a0a0a] border border-[#404040] px-3 py-2 rounded-sm shrink-0"
                >
                  <div className="font-display text-xl text-white leading-none">{u.season_goals}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737373] mt-0.5">
                    Goals {season ? season : "all-time"}
                  </div>
                </div>
              )}
              {minGa90 && u.ga_per90 !== undefined && (
                <div
                  data-testid={`discover-ga90-${u.id}`}
                  className="text-center bg-[#0a0a0a] border border-[#404040] px-3 py-2 rounded-sm shrink-0"
                >
                  <div className="font-display text-xl text-white leading-none">{u.ga_per90}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737373] mt-0.5">G+A /90</div>
                </div>
              )}
              {minMpg && u.minutes_per_game !== undefined && (
                <div
                  data-testid={`discover-mpg-${u.id}`}
                  className="text-center bg-[#0a0a0a] border border-[#404040] px-3 py-2 rounded-sm shrink-0"
                >
                  <div className="font-display text-xl text-white leading-none">{u.minutes_per_game}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#737373] mt-0.5">Mins/game</div>
                </div>
              )}
              {isScout && u.role === "player" && u.id !== me?.id && (
                <ShortlistButton playerId={u.id} shortlists={shortlists} onChange={setShortlists} />
              )}
              <button
                data-testid={`discover-follow-${u.id}`}
                onClick={() => toggle(u)}
                className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
                  u.is_following
                    ? "border border-[#404040] text-white hover:bg-[#1a1a1a]"
                    : "bg-white text-black hover:bg-gray-200"
                }`}
              >
                {u.is_following ? "Following" : "Follow"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
